import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useFirebase } from '../contexts/FirebaseContext';
import { addStaff } from '../services/staffService';
import { saveSettings } from '../services/settingsService';
import { Tenant, PlanId, updateTenant } from '../services/tenantService';
import { stripeService } from '../services/stripeService';
import { getAllPlatformConfigs, savePlatformConfig, deletePlatformConfig } from '../services/platformConfigService';
import { 
  getAllPromoters, 
  addPromoter, 
  updatePromoter, 
  deletePromoter, 
  getPromoterPayouts, 
  addPromoterPayout, 
  Promoter, 
  PromoterPayout 
} from '../services/promoterService';
import { PlatformConfig } from '../types';
import { formatCurrency } from '../lib/formatters';
import { motion, AnimatePresence } from 'motion/react';
import { TipLawsManager } from './admin/TipLawsManager';
import { ResourceManager } from './admin/ResourceManager';
import { ProjectTracker } from './admin/ProjectTracker';
import { SustainabilityDashboard } from './admin/SustainabilityDashboard';
import { 
  ShieldCheck, 
  AlertTriangle, 
  CheckCircle2, 
  Activity, 
  Code, 
  Copy, 
  Sparkles, 
  RotateCcw, 
  Compass, 
  HeartPulse, 
  Info, 
  Layers, 
  Eye, 
  Check, 
  ArrowRight,
  TrendingUp,
  FileText,
  UserPlus,
  Play,
  Share2,
  Gift,
  HelpCircle,
  TrendingDown,
  Building2,
  Users,
  Search,
  Globe,
  RefreshCw,
  Cloud,
  Smartphone,
  Terminal,
  Plus,
  Trash2,
  Save,
  Phone,
  Mail,
  Tag,
  Banknote,
  Star,
  ExternalLink,
  Palette,
  Printer,
  DollarSign,
  Zap,
  Calendar,
  Clock,
  PlusCircle
} from 'lucide-react';

interface Props {
  tenantId: string;
}

const styleConfigs = {
  indigo: {
    primaryBg: 'bg-indigo-600',
    primaryBgHover: 'hover:bg-indigo-700',
    primaryText: 'text-indigo-600',
    lightBg: 'bg-indigo-50/70',
    borderColor: 'border-indigo-150',
    darkText: 'text-indigo-950',
    gradient: 'from-indigo-650 to-blue-600',
  },
  emerald: {
    primaryBg: 'bg-emerald-600',
    primaryBgHover: 'hover:bg-emerald-750',
    primaryText: 'text-emerald-600',
    lightBg: 'bg-emerald-50/70',
    borderColor: 'border-emerald-150',
    darkText: 'text-emerald-950',
    gradient: 'from-emerald-700 to-teal-600',
  },
  charcoal: {
    primaryBg: 'bg-slate-900',
    primaryBgHover: 'hover:bg-slate-800',
    primaryText: 'text-slate-800',
    lightBg: 'bg-slate-100/70',
    borderColor: 'border-slate-200',
    darkText: 'text-slate-950',
    gradient: 'from-slate-900 to-slate-800',
  },
  amber: {
    primaryBg: 'bg-amber-600',
    primaryBgHover: 'hover:bg-amber-700',
    primaryText: 'text-amber-600',
    lightBg: 'bg-amber-50/70',
    borderColor: 'border-amber-150',
    darkText: 'text-amber-950',
    gradient: 'from-amber-600 to-orange-500',
  },
};

interface ChecklistItem {
  id: string;
  category: 'architecture' | 'database' | 'standards' | 'ops';
  title: string;
  description: string;
  checked: boolean;
}

export const SuperAdminPortal: React.FC<Props> = ({ tenantId }) => {
  const { user } = useFirebase();
  const [copiedRuleId, setCopiedRuleId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  
  // Tab control
  const [activeTabName, setActiveTabName] = useState<'clients' | 'referrals' | 'configs' | 'laws' | 'deployments' | 'resources' | 'tracker'>('clients');
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [isHealthChecking, setIsHealthChecking] = useState(false);

  // Promoter states
  const [promoters, setPromoters] = useState<Promoter[]>([]);
  const [isPromotersLoading, setIsPromotersLoading] = useState(false);
  const [newPromoterName, setNewPromoterName] = useState('');
  const [newPromoterEmail, setNewPromoterEmail] = useState('');
  const [newPromoterPhone, setNewPromoterPhone] = useState('');
  const [newPromoterCode, setNewPromoterCode] = useState('');
  const [newPromoterMethod, setNewPromoterMethod] = useState<'Flyer' | 'Word of Mouth' | 'Event' | 'Social Media' | 'Other'>('Flyer');
  const [newPromoterDetails, setNewPromoterDetails] = useState('');
  const [newPromoterCommission, setNewPromoterCommission] = useState('20');
  const [copiedCodeMap, setCopiedCodeMap] = useState<Record<string, boolean>>({});

  // Link Client Venue state
  const [selectedTenantToLink, setSelectedTenantToLink] = useState('');
  const [selectedPromoterCodeToLink, setSelectedPromoterCodeToLink] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  // Payout states
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutPromoter, setPayoutPromoter] = useState<Promoter | null>(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [promoterPayouts, setPromoterPayouts] = useState<PromoterPayout[]>([]);
  const [isPayoutsLoading, setIsPayoutsLoading] = useState(false);

  // Platform Configs state
  const [platformConfigs, setPlatformConfigs] = useState<PlatformConfig[]>([]);
  const [isConfigsLoading, setIsConfigsLoading] = useState(false);
  const [configSearchTerm, setConfigSearchTerm] = useState('');
  const [cKey, setCKey] = useState('');
  const [cCategory, setCCategory] = useState('');
  const [cOptions, setCOptions] = useState('');

  // Benchmark settings state
  const [minSampleSize, setMinSampleSize] = useState<number>(10);
  const [isSavingBenchmarkSettings, setIsSavingBenchmarkSettings] = useState(false);

  // Flyer Customizer Modal states
  const [showFlyerModal, setShowFlyerModal] = useState(false);
  const [flyerPromoter, setFlyerPromoter] = useState<Promoter | null>(null);
  const [flyerStyle, setFlyerStyle] = useState<'indigo' | 'emerald' | 'charcoal' | 'amber'>('indigo');
  const [customCta, setCustomCta] = useState('Claim Your 14-Day Free Trial of TipValue AI');
  const [customNote, setCustomNote] = useState('Experience automated, dispute-free tip pooling with zero credit card required.');
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    try {
      setIsInIframe(window.self !== window.top);
    } catch (e) {
      setIsInIframe(true);
    }
  }, []);

  // One-time plan upgrade for admin@growezy.ai
  useEffect(() => {
    const target = tenantId;
    if (user?.email === 'admin@growezy.ai' && target && target !== 'undefined' && target !== 'null') {
      updateTenant(target, { planId: 'enterprise', subscriptionStatus: 'active' }).then(() => {
        console.log('Plan upgraded to enterprise & active subscription for target ' + target);
      }).catch(err => {
        console.log('Failed one-time plan upgrade:', err);
      });
    }
  }, [user, tenantId]);
  
  // Promoter data fetcher
  const fetchPromotersData = async () => {
    setIsPromotersLoading(true);
    try {
      const list = await getAllPromoters();
      setPromoters(list);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPromotersLoading(false);
    }
  };

  // Platform Configs fetcher
  const fetchPlatformConfigsData = async () => {
    setIsConfigsLoading(true);
    try {
      const list = await getAllPlatformConfigs();
      setPlatformConfigs(list);
    } catch (e) {
      console.error(e);
    } finally {
      setIsConfigsLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotersData();
    fetchPlatformConfigsData();

    // Fetch custom benchmark sample size requirement
    const loadBenchmarkSettings = async () => {
      try {
        const settingsRef = doc(db, 'platform_config', 'benchmark_settings');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const sData = settingsSnap.data();
          if (typeof sData.minSampleSize === 'number') {
            setMinSampleSize(sData.minSampleSize);
          }
        }
      } catch (err) {
        console.error('Error fetching admin benchmark settings:', err);
      }
    };
    loadBenchmarkSettings();
  }, []);

  const handleSaveBenchmarkSettings = async () => {
    setIsSavingBenchmarkSettings(true);
    try {
      const docRef = doc(db, 'platform_config', 'benchmark_settings');
      await setDoc(docRef, { minSampleSize }, { merge: true });
      
      alert(`Benchmark sample size threshold successfully saved to ${minSampleSize}! This threshold has been updated dynamically across all regional cohort views.`);
    } catch (err: any) {
      console.error(err);
      alert('Failed to save benchmark threshold settings: ' + err.message);
    } finally {
      setIsSavingBenchmarkSettings(false);
    }
  };

  // Promoter handlers
  const handleAddPromoter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPromoterName || !newPromoterCode) {
      alert("Name and Unique Code are required.");
      return;
    }
    const cleanCode = newPromoterCode.trim().toUpperCase();
    if (promoters.some(p => p.code === cleanCode)) {
      alert("A promoter with this unique code already exists.");
      return;
    }
    try {
      const newProm: Promoter = {
        id: 'PROM-' + Math.random().toString(36).substring(2, 9).toUpperCase(),
        name: newPromoterName.trim(),
        email: newPromoterEmail.trim().toLowerCase(),
        phone: newPromoterPhone.trim(),
        code: cleanCode,
        referralMethod: newPromoterMethod,
        referralMethodDetails: newPromoterDetails.trim(),
        createdAt: new Date().toISOString(),
        totalPaidCommissions: 0,
        commissionPercentage: Number(newPromoterCommission) || 20
      };
      await addPromoter(newProm);
      setPromoters([newProm, ...promoters]);
      setNewPromoterName('');
      setNewPromoterEmail('');
      setNewPromoterPhone('');
      setNewPromoterCode('');
      setNewPromoterDetails('');
      setNewPromoterCommission('20');
      alert("Promoter registered successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to add promoter.");
    }
  };

  const handleDeletePromoter = async (id: string) => {
    if (!confirm("Are you sure you want to remove this promoter?")) return;
    try {
      await deletePromoter(id);
      setPromoters(promoters.filter(p => p.id !== id));
      alert("Promoter removed.");
    } catch (e) {
      console.error(e);
    }
  };

  const handleLinkTenantToPromoter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenantToLink) {
      alert("Please select a client venue.");
      return;
    }
    try {
      setIsLinking(true);
      await updateTenant(selectedTenantToLink, { referredBy: selectedPromoterCodeToLink || "" });
      setClients(prev => prev.map(c => c.tenantId === selectedTenantToLink ? { ...c, referredBy: selectedPromoterCodeToLink || "" } : c));
      alert(selectedPromoterCodeToLink ? `Linked successfully to promoter code: ${selectedPromoterCodeToLink}` : "Unlinked successfully!");
      setSelectedTenantToLink('');
    } catch (err) {
      console.error(err);
      alert("Failed to update referral relation.");
    } finally {
      setIsLinking(false);
    }
  };

  const handleOpenPayoutModal = async (promoter: Promoter) => {
    setPayoutPromoter(promoter);
    setPayoutAmount('');
    setPayoutNotes('');
    setIsPayoutsLoading(true);
    setShowPayoutModal(true);
    try {
      const history = await getPromoterPayouts(promoter.id);
      setPromoterPayouts(history);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPayoutsLoading(false);
    }
  };

  const handleRecordPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payoutPromoter || !payoutAmount || isNaN(Number(payoutAmount)) || Number(payoutAmount) <= 0) {
      alert("Please enter a valid payout amount.");
      return;
    }
    const amountNum = Number(payoutAmount);
    try {
      const payoutId = 'PAY-' + Math.random().toString(36).substring(2, 9).toUpperCase();
      const newPayout: PromoterPayout = {
        id: payoutId,
        promoterId: payoutPromoter.id,
        amount: amountNum,
        date: new Date().toISOString(),
        notes: payoutNotes.trim()
      };
      
      const newTotalPaid = (payoutPromoter.totalPaidCommissions || 0) + amountNum;
      
      await addPromoterPayout(payoutPromoter.id, newPayout);
      await updatePromoter(payoutPromoter.id, { totalPaidCommissions: newTotalPaid });
      
      setPromoterPayouts([newPayout, ...promoterPayouts]);
      setPromoters(prev => prev.map(p => p.id === payoutPromoter.id ? { ...p, totalPaidCommissions: newTotalPaid } : p));
      setPayoutPromoter(prev => {
        if (!prev) return null;
        return { ...prev, totalPaidCommissions: newTotalPaid };
      });
      setPayoutAmount('');
      setPayoutNotes('');
      alert("Payout recorded successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to log payout.");
    }
  };

  // Config handlers
  const handleSaveConfig = async (config: PlatformConfig) => {
    await savePlatformConfig(config);
    setPlatformConfigs(platformConfigs.map(c => c.key === config.key ? config : c));
    alert(`Config ${config.key} saved successfully!`);
  };

  const handleDeleteConfig = async (key: string) => {
    if (!confirm('Are you sure you want to delete this config?')) return;
    await deletePlatformConfig(key);
    setPlatformConfigs(platformConfigs.filter(c => c.key !== key));
  };

  const handleAddConfig = async () => {
    if (!cKey) return;
    const newConfig: PlatformConfig = {
      key: cKey.trim(),
      category: cCategory.trim(),
      options: cOptions.split(',').map(o => o.trim()).filter(Boolean)
    };
    await savePlatformConfig(newConfig);
    setPlatformConfigs([...platformConfigs, newConfig]);
    setCKey('');
    setCCategory('');
    setCOptions('');
    alert("Platform configuration added!");
  };

  const handleSeedDefaults = async () => {
    const configsToSeed = [
      { key: 'payrollProviders', category: 'General', options: ['ADP', 'Paychex', 'Gusto', 'QuickBooks Payroll', 'Other'] },
      { key: 'payoutMethod', category: 'Tips', options: ['Paycheck', 'Cash', 'Zelle', 'PayPal/Venmo', 'Other'] },
      { key: 'posMerchantProvider', category: 'General', options: ['Toast', 'Square', 'Clover', 'TouchBistro', 'Other'] },
      { key: 'roles', category: 'General', options: ['Manager', 'Server', 'Bartender', 'Host', 'Other'] }
    ];
    for (const config of configsToSeed) {
      await savePlatformConfig(config);
    }
    const list = await getAllPlatformConfigs();
    setPlatformConfigs(list);
    alert('Default configurations seeded successfully!');
  };

  // Clients Real-Time Lookup State
  const [clients, setClients] = useState<Tenant[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Trial' | 'Inactive'>('All');
  const [planFilter, setPlanFilter] = useState<'All' | 'base' | 'enhanced' | 'enterprise'>('All');
  const [updatingTenantId, setUpdatingTenantId] = useState<string | null>(null);

  const handleExtendTrial = async (client: Tenant, daysToAdd: number) => {
    setUpdatingTenantId(client.tenantId);
    try {
      const now = new Date();
      const currentEnd = client.trialEndsAt?.toDate 
        ? client.trialEndsAt.toDate() 
        : client.trialEndsAt ? new Date(client.trialEndsAt) : now;
      
      const baseDate = currentEnd > now ? currentEnd : now;
      const newEndDate = new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

      await updateTenant(client.tenantId, {
        trialEndsAt: newEndDate,
        status: 'Trial',
        subscriptionStatus: 'trialing'
      });

      // Update local state
      setClients(prev => prev.map(c => c.tenantId === client.tenantId ? {
        ...c,
        trialEndsAt: newEndDate,
        status: 'Trial',
        subscriptionStatus: 'trialing'
      } : c));

      alert(`Extended trial for "${client.name || client.tenantId}" by ${daysToAdd} days! New end date: ${newEndDate.toLocaleDateString()}`);
    } catch (err: any) {
      console.error('Error extending trial:', err);
      alert(`Failed to extend trial: ${err.message || 'Unknown error'}`);
    } finally {
      setUpdatingTenantId(null);
    }
  };

  const handleChangeClientPlan = async (client: Tenant, newPlan: PlanId) => {
    setUpdatingTenantId(client.tenantId);
    try {
      await updateTenant(client.tenantId, { planId: newPlan });
      setClients(prev => prev.map(c => c.tenantId === client.tenantId ? { ...c, planId: newPlan } : c));
    } catch (err: any) {
      console.error('Error changing plan:', err);
      alert(`Failed to update plan: ${err.message || 'Unknown error'}`);
    } finally {
      setUpdatingTenantId(null);
    }
  };

  const fetchClients = async () => {
    setIsLoadingClients(true);
    try {
      const tenantsRef = collection(db, 'tenants');
      const querySnapshot = await getDocs(tenantsRef);
      const list = querySnapshot.docs.map(doc => ({
        tenantId: doc.id,
        ...doc.data()
      } as Tenant));
      setClients(list);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setIsLoadingClients(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [tenantId]);

  // Derived filtered clients
  const filteredClients = clients.filter(client => {
    const searchLower = clientSearch.toLowerCase();
    const matchesSearch = !clientSearch ||
      (client.name || '').toLowerCase().includes(searchLower) ||
      (client.contactEmail || '').toLowerCase().includes(searchLower) ||
      (client.ein || '').toLowerCase().includes(searchLower) ||
      (client.merchantId || '').toLowerCase().includes(searchLower) ||
      (client.dbaName || '').toLowerCase().includes(searchLower) ||
      (client.locationName || '').toLowerCase().includes(searchLower);

    const matchesStatus = statusFilter === 'All' || client.status === statusFilter;
    const matchesPlan = planFilter === 'All' || client.planId === planFilter;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Dynamic grassroots promoter referral calculations
  const totalPromoters = promoters.length;
  const totalReferredClientsCount = clients.filter(c => c.referredBy).length;

  const getPromoterReferredClientsCount = (promCode: string) => {
    return clients.filter(c => c.referredBy === promCode).length;
  };

  const getClientMonthlySubscription = (planId: string) => {
    if (planId === 'enterprise') return 499;
    if (planId === 'enhanced') return 199;
    return 99; // base plan
  };

  const getPromoterMonthlyMRR = (promCode: string) => {
    const promoter = promoters.find(p => p.code === promCode);
    const pct = promoter && typeof promoter.commissionPercentage === 'number' ? promoter.commissionPercentage : 20;
    const activeRefs = clients.filter(c => c.referredBy === promCode);
    return activeRefs.reduce((acc, c) => acc + (getClientMonthlySubscription(c.planId || 'base') * (pct / 100)), 0);
  };

  const calculatePromoterEarnedToDate = (promoter: Promoter) => {
    const pct = typeof promoter.commissionPercentage === 'number' ? promoter.commissionPercentage : 20;
    const activeRefs = clients.filter(c => c.referredBy === promoter.code);
    return activeRefs.reduce((acc, c) => {
      let monthsActive = 1;
      if (c.createdAt) {
        try {
          let createdTime: number = NaN;
          if (c.createdAt && typeof c.createdAt.toDate === 'function') {
            createdTime = c.createdAt.toDate().getTime();
          } else if (c.createdAt && typeof c.createdAt.seconds === 'number') {
            createdTime = c.createdAt.seconds * 1000;
          } else {
            createdTime = new Date(c.createdAt).getTime();
          }
          if (!isNaN(createdTime)) {
            const msPassed = new Date().getTime() - createdTime;
            monthsActive = Math.max(1, Math.round(msPassed / (1000 * 60 * 60 * 24 * 30)));
          }
        } catch (e) {
          monthsActive = 1;
        }
      }
      const monthlyRate = getClientMonthlySubscription(c.planId || 'base');
      const fee = monthlyRate * (pct / 100) * monthsActive;
      return acc + fee;
    }, 0);
  };

  const totalEarnedCommissionsAll = promoters.reduce((acc, p) => acc + calculatePromoterEarnedToDate(p), 0);
  const totalPaidCommissionsAll = promoters.reduce((acc, p) => acc + (p.totalPaidCommissions || 0), 0);
  const totalDueCommissionsAll = Math.max(0, totalEarnedCommissionsAll - totalPaidCommissionsAll);

  // Initial guidelines from AGENTS.md & TRANSACTION_LOGIC.md
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    {
      id: 'no_inline_filters',
      category: 'architecture',
      title: 'Rule of No Inline Page Filtering',
      description: 'Never filter or categorize transactions with inline conditional statements on visual dashboards. Use transactionUtils helpers.',
      checked: true,
    },
    {
      id: 'explicit_storage',
      category: 'database',
      title: 'Rule of Explicit Field Storage',
      description: 'Ensure backend importers/services write explicit reportingStatus, type, and source properties to Firestore at key transactions.',
      checked: true,
    },
    {
      id: 'central_helpers',
      category: 'architecture',
      title: 'Standardize ledger verification helpers',
      description: 'All payroll formats, reports, and detail viewers share the exact transactionUtils.ts checks (isSubmitted, isAllocatedCash, etc.).',
      checked: true,
    },
    {
      id: 'only_lucide_icons',
      category: 'standards',
      title: 'Strict Lucide Icon Set Compliance',
      description: 'Exclude generic inline SVGs or secondary font icon sets. Rely strictly on clean icons imported from lucide-react.',
      checked: true,
    },
    {
      id: 'motion_package',
      category: 'standards',
      title: 'Standardized motion Package for UX',
      description: "UX elements utilize layout and timing transitions standardizing imports strictly from 'motion/react'.",
      checked: true,
    },
    {
      id: 'no_theme_switchers',
      category: 'standards',
      title: 'Single Solid Design Theme Focus',
      description: 'Establish high visual trust using one consistent, polished high-contrast theme rather than custom in-app presets.',
      checked: true,
    },
    {
      id: 'pre_commit_checks',
      category: 'ops',
      title: 'Automated Type Checker Guard',
      description: 'Before committing revisions, execute TypeScript verification (tsc --noEmit) and lint validation tasks.',
      checked: false,
    },
    {
      id: 'dispute_transitions',
      category: 'ops',
      title: 'Re-verify dispute state transitions',
      description: 'Periodically review that approved disputes update reportingStatus successfully rather than remaining orphaned.',
      checked: false,
    },
    {
      id: 'backup_rollback_plan',
      category: 'ops',
      title: 'Periodic rollback drill checkups',
      description: 'Demonstrate active compliance by validating transaction batchId boundaries and rollback sequences.',
      checked: false,
    }
  ]);

  // Load persistence states
  useEffect(() => {
    const loadPortalProgress = async () => {
      try {
        const portalRef = doc(db, 'platform_config', 'super_admin_portal');
        const snap = await getDoc(portalRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.checklist) {
            setChecklist(prev => 
              prev.map(item => {
                const found = data.checklist.find((c: any) => c.id === item.id);
                return found ? { ...item, checked: !!found.checked } : item;
              })
            );
          }
        }
      } catch (err) {
        console.error('Error loading super admin portal checklist:', err);
      }
    };
    loadPortalProgress();
  }, [user]);

  const toggleChecklist = (id: string) => {
    setChecklist(prev => prev.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  const handleSavePortal = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const portalRef = doc(db, 'platform_config', 'super_admin_portal');
      await setDoc(portalRef, { 
        checklist: checklist.map(c => ({ id: c.id, checked: c.checked })),
        updatedAt: new Date().toISOString(),
        updatedBy: user?.email || 'super_admin'
      }, { merge: true });
      setSaveStatus('Portal tracking saved successfully!');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Error saving super admin checklist:', err);
      setSaveStatus('Error saving checklist state.');
    } finally {
      setIsSaving(false);
    }
  };

  const checkSystemHealth = async () => {
    setIsHealthChecking(true);
    try {
      const res = await fetch('/api/system-health');
      const data = await res.json();
      setSystemHealth(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsHealthChecking(false);
    }
  };

  // Heuristic Appraisal variables
  const [proposalText, setProposalText] = useState('');
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [simulatorResult, setSimulatorResult] = useState<{
    status: 'PASSED' | 'CAUTION' | 'FAILED';
    rating: string;
    explanation: string;
    ruleVioliated?: string;
  } | null>(null);

  // Preset Scenario Appraisals
  const scenarios = [
    {
      id: 'theme_toggle',
      label: 'Feature: Custom Neon Palette Theme Switcher',
      text: 'Add a configuration setting and a header button that lets users toggle between a Dark Violet, Retro Slate, and Autumn Amber color theme across the app.',
      analysis: {
        status: 'CAUTION' as const,
        rating: '⚠️ Theme-Selection Caution',
        explanation: 'Adding visual preset swappers introduces structural UI complexity. Our guidelines recommend choosing ONE polished, high-contrast visual theme and sticking to it. Bypassing this adds unrequested features that complicate rendering consistency.',
      }
    },
    {
      id: 'inline_filter',
      label: 'Fix: Quick Inline Filter on Audit Screen',
      text: "To fix a minor view error on the staff overview page, we filter row allocations inline: filteredTxs = txs.filter(t => t.source === 'Cash' || (t.type === 'Allocated' && t.submissionStatus !== 'Rejected'))",
      analysis: {
        status: 'FAILED' as const,
        rating: '❌ Rule Violation (Critical)',
        ruleVioliated: 'Golden Rule #1: No Inline Filtering',
        explanation: 'This directly violates the absolute priority directive! You are STRICTLY FORBIDDEN from writing custom, inline conditions to calculate or group ledger states on individual pages. You must import and utilize standard indicators from src/lib/transactionUtils.ts. Modifying this in isolation triggers reporting drift between pages!',
      }
    },
    {
      id: 'explicit_importer',
      label: 'Optimization: Store explicit status on bulk CSV imports',
      text: "Modify posImportService.ts to explicitly calculate and populate document.reportingStatus = 'pos_submitted' or 'pos_allocated' during initial ledger writes.",
      analysis: {
        status: 'PASSED' as const,
        rating: '✨ Blueprint Compliant',
        explanation: 'Perfect! This adheres fully to Rule #2 (Explicit Storage). Storing core properties on immediate database writes ensures that downstream UI dashboards consume standardized properties without needing to complex-guess or backtrack records at runtime.',
      }
    },
    {
      id: 'inline_svg_icons',
      label: 'Asset: Design custom vector brand badges inline',
      text: 'Creating custom SVG react components inline to render illustrative medals next to top-earning service employees.',
      analysis: {
        status: 'FAILED' as const,
        rating: '❌ Asset Standards Violation',
        explanation: 'Violates our UI standards constraint. Inline SVG paths duplicate render code and swell bundle footprints unnecessarily. All custom widgets or indicators must look elegant and import icons strictly from lucide-react.',
      }
    },
  ];

  const handleSelectScenario = (id: string) => {
    setSelectedScenario(id);
    const scen = scenarios.find(s => s.id === id);
    if (scen) {
      setProposalText(scen.text);
      setSimulatorResult(scen.analysis);
    } else {
      setProposalText('');
      setSimulatorResult(null);
    }
  };

  const runCustomSimulator = () => {
    if (!proposalText.trim()) return;

    const query = proposalText.toLowerCase();
    let status: 'PASSED' | 'CAUTION' | 'FAILED' = 'PASSED';
    let rating = '✨ Blueprint Compliant';
    let explanation = 'Your proposed change looks sustainable! It preserves modular boundaries, relies on standard parameters, and avoids adding complex unrequested settings.';

    if (query.includes('filter(') && (query.includes('source') || query.includes('status') || query.includes('type'))) {
      status = 'FAILED';
      rating = '❌ Rule Violation (Critical)';
      explanation = 'Heuristic alert: Detected an on-the-fly filtering expression. Standard page summaries, details, and reports MUST NOT run inline boolean filters. Route your query through helper functions inside src/lib/transactionUtils.ts instead.';
    } else if (query.includes('svg') || query.includes('<path') || query.includes('font-awesome')) {
      status = 'FAILED';
      rating = '❌ Icon Standard Violation';
      explanation = 'Heuristic alert: Custom inline SVG and external font tags detected. You are required to rely fully on clean vector iconography from the loaded lucide-react package to avoid bloating code files.';
    } else if (query.includes('theme') || query.includes('dark mode') || query.includes('toggle-style')) {
      status = 'CAUTION';
      rating = '⚠️ Theme Switcher warning';
      explanation = 'Heuristic alert: You have proposed an interactive theme switcher. Our rules request maintaining a single modern, professional, high-contrast visual interface out of the box to prevent design bloat.';
    } else if (query.includes('reportingstatus') || query.includes('db.') || query.includes('collection') || query.includes('firestore')) {
      if (query.includes('explicit') || query.includes('store') || query.includes('write')) {
        status = 'PASSED';
        rating = '✨ Excellent Database Practice';
        explanation = 'Your database flow correctly attempts to write explicit state flags (explicit reportingStatus). This keeps transactional records clean and prevents the frontend from guessing properties.';
      }
    }

    setSimulatorResult({
      status,
      rating,
      explanation
    });
  };

  const handleCopyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedRuleId(id);
    setTimeout(() => setCopiedRuleId(null), 2000);
  };

  // Metrics
  const totalItems = checklist.length;
  const completedItems = checklist.filter(c => c.checked).length;
  const scorePct = Math.round((completedItems / totalItems) * 100);

  const getHealthGrade = (pct: number) => {
    if (pct === 100) return 'A+ Outstanding';
    if (pct >= 90) return 'A Stable';
    if (pct >= 80) return 'B Highly Secure';
    if (pct >= 70) return 'C Functional';
    return 'D At Risk';
  };

  return (
    <>
      <div className="space-y-8 font-sans text-gray-800 super-admin-main-content">
      
      {/* Portal Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6 no-print">
        <div>
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-800 text-[10px] font-black uppercase px-2 py-0.5 rounded">Super Admin Dashboard</span>
            <div className="text-xs text-emerald-600 font-bold flex items-center gap-1">
              <Activity className="w-3.5 h-3.5 animate-pulse" /> Live System Monitor
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight uppercase mt-1">
            Core Architecture & Sustainability Center
          </h1>
          <p className="text-gray-500 text-xs md:text-sm mt-1 max-w-2xl font-medium">
            Monitor transaction health, review system standards, and simulation-test code proposals to preserve transaction integrity.
          </p>
        </div>
        <button
          onClick={handleSavePortal}
          disabled={isSaving}
          className="btn-primary hover:scale-[1.02] flex items-center gap-2 self-start md:self-auto shrink-0 shadow-lg"
        >
          {isSaving ? 'Syncing...' : 'Save Tracked Progress'}
        </button>
      </div>

      {saveStatus && (
        <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-bold py-3 px-4 rounded-xl flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          {saveStatus}
        </div>
      )}

      {/* SEGMENTED TAB SELECTOR */}
      <div className="flex border-b border-gray-200 bg-gray-100/60 p-1.5 rounded-2xl max-w-2xl mt-2 gap-1 no-print">
        <button
          onClick={() => setActiveTabName('clients')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'clients'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Clients & Activations
        </button>
        <button
          onClick={() => setActiveTabName('referrals')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'referrals'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Grassroots Referrals (20%)
        </button>
        <button
          onClick={() => setActiveTabName('configs')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'configs'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Platform Configs
        </button>
        <button
          onClick={() => setActiveTabName('laws')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'laws'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Tip Laws
        </button>
        <button
          onClick={() => setActiveTabName('deployments')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'deployments'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Deployments
        </button>
        <button
          onClick={() => setActiveTabName('resources')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'resources'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Resources
        </button>
        <button
          onClick={() => setActiveTabName('tracker')}
          className={`flex-1 py-2 text-center text-xs font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTabName === 'tracker'
              ? 'bg-white text-slate-900 shadow font-black'
              : 'text-gray-500 hover:text-gray-900 font-bold'
          }`}
        >
          Project Tracker
        </button>
      </div>

      {activeTabName === 'tracker' && <ProjectTracker />}

      {activeTabName === 'clients' && (
        <>
      {/* REGISTERED CLIENTS AND SETUP DIR - REAL-TIME FIRESTORE COUNT */}
      <div id="setup-clients-dashboard" className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-200 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="bg-blue-50 text-blue-800 text-[9px] font-black uppercase px-2.5 py-1 rounded border border-blue-200/50">
                ACTIVE CLIENT MONITOR
              </span>
              <div className="text-[10px] text-gray-450 font-extrabold flex items-center gap-1">
                <Globe className="w-3.5 h-3.5 text-blue-500" /> Live Firestore Connection
              </div>
            </div>
            <h2 className="text-xl font-black text-gray-901 tracking-tight uppercase italic flex items-center gap-2">
              <Building2 className="w-5.5 h-5.5 text-gray-850" />
              Registered Client Directory ({filteredClients.length})
            </h2>
            <p className="text-gray-500 text-xs font-semibold leading-relaxed">
              As the TipValue Super Admin, you can monitor the setup metrics and subscription phases of all organizations across the platform in real-time.
            </p>
          </div>
          <button
            onClick={fetchClients}
            disabled={isLoadingClients}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-750 hover:text-gray-950 font-black uppercase text-[10px] tracking-wider rounded-xl border border-gray-200 transition-all cursor-pointer shadow-sm self-start sm:self-auto shrink-0"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoadingClients ? 'animate-spin' : ''}`} />
            {isLoadingClients ? 'Refreshing...' : 'Refresh Tenants'}
          </button>
        </div>

        {/* Dynamic Aggregated Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-slate-900 rounded-3xl p-6 text-white text-center">
          <div>
            <div className="text-2xl md:text-3xl font-black text-blue-400 leading-none">{clients.length}</div>
            <div className="text-[8px] md:text-[9px] text-gray-400 font-extrabold uppercase tracking-widest mt-1.5 flex items-center justify-center gap-1">
              Set Up Clients
            </div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-black text-emerald-400 leading-none">
              {clients.filter(c => c.status === 'Active').length}
            </div>
            <div className="text-[8px] md:text-[9px] text-gray-400 font-extrabold uppercase tracking-widest mt-1.5">
              Active Subs
            </div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-black text-amber-400 leading-none">
              {clients.filter(c => c.status === 'Trial').length}
            </div>
            <div className="text-[8px] md:text-[9px] text-gray-400 font-extrabold uppercase tracking-widest mt-1.5">
              Active Trials
            </div>
          </div>
          <div>
            <div className="text-2xl md:text-3xl font-black text-purple-400 leading-none">
              {clients.filter(c => c.planId === 'enterprise').length}
            </div>
            <div className="text-[8px] md:text-[9px] text-gray-400 font-extrabold uppercase tracking-widest mt-1.5">
              Enterprise Accounts
            </div>
          </div>
        </div>

        {/* Directory Controls */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by business name, email, EIN, merchant ID..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e: any) => setStatusFilter(e.target.value)}
              className="px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-bold uppercase bg-white text-gray-700 focus:outline-none cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active Only</option>
              <option value="Trial">Trial Only</option>
              <option value="Inactive">Inactive Only</option>
            </select>
            <select
              value={planFilter}
              onChange={(e: any) => setPlanFilter(e.target.value)}
              className="px-3.5 py-2 border border-gray-200 rounded-xl text-xs font-bold uppercase bg-white text-gray-700 focus:outline-none cursor-pointer"
            >
              <option value="All">All Plans</option>
              <option value="base">Base Plan</option>
              <option value="enhanced">Enhanced Plan</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>

        {/* Directory Data Grid */}
        {isLoadingClients ? (
          <div className="py-16 text-center text-gray-400 font-bold uppercase text-xs tracking-widest flex flex-col items-center justify-center gap-3">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mb-2" />
            Connecting to Firestore and loading client indexes...
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="py-16 text-center bg-gray-50 rounded-[2rem] border border-dashed border-gray-200">
            <p className="text-xs text-gray-600 font-extrabold uppercase">No registered clients match your query</p>
            <p className="text-[11px] text-gray-400 mt-1 font-semibold">Try clearing your filters or adjusting your search phrase.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[1.5rem] border border-gray-200 shadow-sm">
            <table className="w-full text-xs text-left text-gray-600 divide-y divide-gray-100">
              <thead className="bg-gray-50 text-[10px] font-black uppercase text-gray-400 tracking-wider">
                <tr>
                  <th className="px-6 py-4">Client / Business Location</th>
                  <th className="px-6 py-4">Tenant Code & Tax Identity</th>
                  <th className="px-6 py-4">Tier & Subscription</th>
                  <th className="px-6 py-4">Primary Contact Details</th>
                  <th className="px-6 py-4">Trial & Beta Management</th>
                  <th className="px-6 py-4 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredClients.map((client) => {
                  const now = new Date();
                  const trialEnd = client.trialEndsAt?.toDate 
                    ? client.trialEndsAt.toDate() 
                    : client.trialEndsAt ? new Date(client.trialEndsAt) : null;
                  const daysLeft = trialEnd && !isNaN(trialEnd.getTime()) 
                    ? Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) 
                    : null;
                  const isUpdating = updatingTenantId === client.tenantId;

                  return (
                  <tr key={client.tenantId} className="hover:bg-gray-50/40 transition-colors">
                    <td className="px-6 py-5 space-y-1">
                      <div>
                        <span className="font-extrabold text-gray-900 uppercase block text-[13px] tracking-tight">
                          {client.name || 'Unnamed Client'}
                        </span>
                        {client.dbaName && (
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block mt-0.5">
                            DBA: {client.dbaName}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 pt-1">
                        <span className="bg-slate-100 text-slate-800 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded">
                          {client.businessType || 'Other'}
                        </span>
                        {client.locationName && (
                          <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                            • {client.locationName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5 font-mono text-[11px] text-gray-600 space-y-1">
                      <div>
                        <span className="text-[9px] text-gray-400 uppercase font-black tracking-wide block">Tenant Key</span>
                        <span className="font-bold text-gray-800">{client.tenantId}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 pt-1 border-t border-gray-50 mt-1">
                        {client.ein && (
                          <div>
                            <span className="text-[8px] text-gray-400 uppercase font-black block">EIN</span>
                            <span className="text-gray-700">{client.ein}</span>
                          </div>
                        )}
                        {client.merchantId && (
                          <div>
                            <span className="text-[8px] text-gray-400 uppercase font-black block">MID</span>
                            <span className="text-gray-700">{client.merchantId}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <select
                          disabled={isUpdating}
                          value={client.planId || 'base'}
                          onChange={(e) => handleChangeClientPlan(client, e.target.value as PlanId)}
                          className="bg-blue-50 text-blue-800 border border-blue-200 text-[10px] font-black uppercase px-2 py-1 rounded cursor-pointer hover:bg-blue-100 transition-colors focus:ring-1 focus:ring-blue-500 outline-none"
                          title="Change Plan Tier"
                        >
                          <option value="base">Base Plan</option>
                          <option value="enhanced">Enhanced Plan</option>
                          <option value="enterprise">Enterprise Plan</option>
                        </select>
                      </div>
                      <div className="text-[10px] text-gray-500 font-bold block">
                        Phase: <span className="text-gray-800 uppercase font-black">{client.subscriptionStatus || 'None'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 space-y-1">
                      {client.contactName && (
                        <div className="text-gray-900 font-extrabold text-[12px] block">{client.contactName}</div>
                      )}
                      <div className="text-[10px] text-gray-400 font-bold space-y-0.5">
                        {client.contactEmail && (
                          <div className="block font-mono">{client.contactEmail}</div>
                        )}
                        {client.contactPhone && (
                          <div className="block font-mono">{client.contactPhone}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-[11px]">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="font-bold text-gray-700">
                          {daysLeft !== null 
                            ? (daysLeft > 0 ? `${daysLeft} days left` : 'Trial ended') 
                            : 'No trial date'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          disabled={isUpdating}
                          onClick={() => handleExtendTrial(client, 30)}
                          className="inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-[9px] font-black uppercase px-2 py-1 rounded transition-colors shadow-xs"
                          title="Add 30 Days to Trial"
                        >
                          <PlusCircle className="w-3 h-3" /> +30 Days
                        </button>
                        <button
                          disabled={isUpdating}
                          onClick={() => handleExtendTrial(client, 14)}
                          className="inline-flex items-center gap-1 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-[9px] font-black uppercase px-1.5 py-1 rounded transition-colors"
                          title="Add 14 Days to Trial"
                        >
                          +14d
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <span className={`inline-block text-[9px] font-black uppercase px-3 py-1 rounded-full border ${
                        client.status === 'Active' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                          : client.status === 'Trial'
                          ? 'bg-blue-50 text-blue-700 border-blue-100'
                          : 'bg-gray-50 text-gray-500 border-gray-150'
                      }`}>
                        {client.status || 'Inactive'}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {/* GRASSROOTS REFERRALS TAB CONTENT */}
      {activeTabName === 'referrals' && (
        <div className="space-y-6">
          {/* Summary cards row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Active Promoters card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-2">
              <div className="text-[10px] uppercase font-black text-slate-450">Grassroots Affiliates</div>
              <div className="text-2xl font-black text-slate-800">{totalPromoters} Active</div>
              <p className="text-[10px] text-gray-550 font-semibold leading-normal">Flyers, Word of mouth & Ground campaigns</p>
            </div>
            {/* Referred Venues card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-2">
              <div className="text-[10px] uppercase font-black text-slate-400">Referred client venues</div>
              <div className="text-2xl font-black text-slate-800">{totalReferredClientsCount} Venues</div>
              <p className="text-[10px] text-gray-500 font-semibold leading-normal">Venues linked to promo codes</p>
            </div>
            {/* Monthly MRR Commission card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-2">
              <div className="text-[10px] uppercase font-black text-slate-400">Total Commissions Earned</div>
              <div className="text-2xl font-black text-indigo-700">{formatCurrency(totalEarnedCommissionsAll)}</div>
              <p className="text-[10px] text-indigo-500 font-semibold leading-normal">Calculated based on 20% recurring fee</p>
            </div>
            {/* Unpaid commission card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-2">
              <div className="text-[10px] uppercase font-black text-slate-400">Total Unpaid Balance</div>
              <div className="text-2xl font-black text-emerald-600">{formatCurrency(totalDueCommissionsAll)} Due</div>
              <p className="text-[10px] text-emerald-500 font-semibold leading-normal">Earned commissions minus recorded payouts</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column (8 cols): list of active promoters */}
            <div className="lg:col-span-8 space-y-4">
              
              {/* Link Client to Promoters Widget */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                <div>
                  <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                    <Tag className="w-4 h-4 text-amber-500" /> Link Client Venue to Grassroots Promoter
                  </h2>
                  <p className="text-[11px] text-gray-500">Associate active subscription venues with grassroots campaign promo codes.</p>
                </div>
                
                <form onSubmit={handleLinkTenantToPromoter} className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="grow space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">1. Select Venue Client</label>
                    <select
                      value={selectedTenantToLink}
                      onChange={(e) => setSelectedTenantToLink(e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    >
                      <option value="">-- Choose Client --</option>
                      {clients.map(c => (
                        <option key={c.tenantId} value={c.tenantId}>
                          {c.name || c.dbaName || c.locationName} ({c.tenantId}) - {(c.planId || 'base').toUpperCase()} Plan {c.referredBy ? `[Currently: ${c.referredBy}]` : '[Unassigned]'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grow space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">2. Assign Promoter campaign</label>
                    <select
                      value={selectedPromoterCodeToLink}
                      onChange={(e) => setSelectedPromoterCodeToLink(e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    >
                      <option value="">-- No promoter / Unlink --</option>
                      {promoters.map(p => (
                        <option key={p.id} value={p.code}>
                          {p.name} ({p.code}) - {p.referralMethod}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isLinking}
                    className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white font-bold rounded-lg text-xs uppercase hover:bg-blue-700 disabled:opacity-50 tracking-wider h-10 shrink-0 cursor-pointer"
                  >
                    {isLinking ? 'Linking...' : 'Apply Link'}
                  </button>
                </form>
              </div>

              {/* Promoters Directory */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-150 pb-3">
                  <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest">Grassroots promoters directory</h2>
                  <span className="text-[10px] bg-blue-50 text-blue-700 border border-blue-100 font-extrabold px-2 py-0.5 rounded-full flex items-center gap-1 uppercase no-print">
                    <Printer className="w-3 h-3" /> Printing Supported
                  </span>
                </div>

                {/* Printing Tips Callout Banner */}
                <div className="p-4 bg-amber-50/50 border border-amber-200/60 rounded-xl space-y-3 text-xs text-amber-900 no-print">
                  <div className="flex items-center gap-2 font-black text-amber-800 uppercase tracking-wider text-[10px]">
                    <Printer className="w-4 h-4 text-amber-500 animate-pulse" />
                    Promo Flyer Printing Guide
                  </div>
                  <p className="text-[11px] text-amber-800/80 leading-relaxed font-semibold">
                    Our customized promo flyers are tailored for high-quality standard physical printing. To ensure the flyer compiles, layout matches perfectly, and the QR code is generated with clean high-contrast boundaries:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-[11px] text-amber-800/85 list-disc pl-4 font-medium leading-relaxed">
                    <li><strong className="font-extrabold text-amber-900">Enable Background Graphics:</strong> In the browser's print dialog, look under &ldquo;More Settings&rdquo; and check <span className="font-bold text-amber-900">Background graphics</span>. Otherwise, colors and design gradients will be omitted.</li>
                    <li><strong className="font-extrabold text-amber-900">Set Margins to None:</strong> Select <span className="font-bold text-amber-900">None</span> or <span className="font-bold text-amber-900">Minimum</span> in the Margins dropdown to let the background expand to fill the complete Letter sheet.</li>
                    <li><strong className="font-extrabold text-amber-900">Paper Layout & Size:</strong> Choose standard <span className="font-bold text-amber-900">Portrait</span> orientation and set size strictly to standard <span className="font-bold text-amber-900">Letter (8.5&rdquo; x 11&rdquo;)</span>.</li>
                    <li><strong className="font-extrabold text-amber-900">Print Scale:</strong> Keep scale at exactly <span className="font-bold text-amber-900">100%</span> (or &ldquo;Actual Size&rdquo;) to prevent the layout from shrinking, warping, or bleeding onto a second page.</li>
                  </ul>
                  <div className="pt-2 border-t border-amber-200/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[10px] text-amber-700 font-semibold font-mono">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                      Paper Quality Recommendation: Heavy cardstock (80lb - 100lb glossy cover)
                    </span>
                    <span className="text-amber-800 bg-amber-200/40 px-2 py-0.5 rounded-md font-sans">
                      Tip: Click &ldquo;Customize Flyer&rdquo; below to preview & print!
                    </span>
                  </div>
                </div>
                {isPromotersLoading ? (
                  <div className="text-center py-8 text-xs text-slate-400">Loading directory...</div>
                ) : promoters.length === 0 ? (
                  <div className="text-center py-8 border border-dashed rounded-xl border-gray-200 space-y-2">
                    <p className="text-xs text-gray-400 font-semibold">No promoters registered yet.</p>
                    <p className="text-[10px] text-gray-400">Use the registration portal on the right to list your first promoter.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {promoters.map(prom => {
                      const count = getPromoterReferredClientsCount(prom.code);
                      const mrrFee = getPromoterMonthlyMRR(prom.code);
                      const earned = calculatePromoterEarnedToDate(prom);
                      const due = Math.max(0, earned - (prom.totalPaidCommissions || 0));
                      return (
                        <div key={prom.id} className="p-4 rounded-xl border border-gray-200 hover:border-blue-300 transition-all space-y-3 bg-slate-50/50">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-1 py-1 px-2.5 bg-indigo-50 border border-indigo-100 text-indigo-700 font-black tracking-tight text-[10px] rounded-full self-start inline-block">
                                PROMO CODE: {prom.code}
                              </div>
                              <h3 className="font-extrabold text-sm text-slate-800 mt-1">{prom.name}</h3>
                              <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                                <span className="flex items-center gap-0.5"><Mail className="w-3 h-3" /> {prom.email}</span>
                                {prom.phone && <span className="flex items-center gap-0.5"><Phone className="w-3 h-3" /> {prom.phone}</span>}
                              </div>
                            </div>
                            
                            <button
                              onClick={() => handleDeletePromoter(prom.id)}
                              className="text-gray-400 hover:text-rose-500 transition-colors p-1"
                              title="Delete Promoter"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Referral Method Badge */}
                          <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100 mt-2 animate-in fade-in duration-300">
                            <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 font-extrabold px-2 py-0.5 rounded">
                              Rate: {prom.commissionPercentage ?? 20}%
                            </span>
                            <span className="text-[10px] bg-slate-200 text-slate-705 font-extrabold px-2 py-0.5 rounded">
                              Method: {prom.referralMethod}
                            </span>
                            {prom.referralMethodDetails && (
                              <span className="text-[10px] text-gray-500 italic">
                                &ldquo;{prom.referralMethodDetails}&rdquo;
                              </span>
                            )}
                          </div>

                          {/* Financial tracker columns */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-3 bg-white border border-gray-100 rounded-lg mt-3 text-[11px] font-semibold leading-normal">
                            <div>
                              <span className="text-[9px] text-gray-400 uppercase font-bold block">Linked Venues</span>
                              <span className="text-slate-800 font-black">{count}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-gray-400 uppercase font-bold block">Active MRR Commission</span>
                              <span className="text-indigo-600 font-black">{formatCurrency(mrrFee)}/mo</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-gray-400 uppercase font-bold block">Total Earned</span>
                              <span className="text-slate-800 font-black">{formatCurrency(earned)}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-gray-400 uppercase font-bold block">Pending Due</span>
                              <span className="text-emerald-600 font-black">{formatCurrency(due)}</span>
                            </div>
                          </div>

                          <div className="flex justify-end flex-wrap gap-2 pt-1">
                            <button
                              onClick={() => {
                                const shareUrl = `https://tipvalue.com/?code=${prom.code}`;
                                navigator.clipboard.writeText(shareUrl);
                                setCopiedCodeMap(prev => ({ ...prev, [prom.code]: true }));
                                setTimeout(() => {
                                  setCopiedCodeMap(prev => ({ ...prev, [prom.code]: false }));
                                }, 2000);
                              }}
                              className="px-2.5 py-1.5 bg-indigo-50 border border-indigo-205 text-indigo-700 font-bold text-[10px] rounded hover:bg-indigo-100 flex items-center gap-1 cursor-pointer uppercase"
                            >
                              {copiedCodeMap[prom.code] ? (
                                <>
                                  <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied Link!
                                </>
                              ) : (
                                <>
                                  <Share2 className="w-3.5 h-3.5" /> Copy Campaign Link
                                </>
                              )}
                            </button>

                            <button
                              onClick={() => {
                                setFlyerPromoter(prom);
                                setShowFlyerModal(true);
                              }}
                              className="px-2.5 py-1.5 bg-indigo-600 text-white font-bold text-[10px] rounded hover:bg-indigo-700 flex items-center gap-1 cursor-pointer uppercase"
                            >
                              <Sparkles className="w-3.5 h-3.5" /> Customize Flyer
                            </button>

                            <a
                              href="/promoter-portal"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2.5 py-1.5 bg-slate-100 border border-gray-200 text-slate-700 font-bold text-[10px] rounded hover:bg-slate-200 flex items-center gap-1 uppercase"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> Partner Dash
                            </a>

                            <button
                              onClick={() => handleOpenPayoutModal(prom)}
                              className="px-3 py-1.5 bg-slate-800 text-white font-bold text-[10px] rounded hover:bg-slate-900 flex items-center gap-1 cursor-pointer uppercase"
                            >
                              <Banknote className="w-3.5 h-3.5" /> Payouts & Logging ({formatCurrency(prom.totalPaidCommissions || 0)} Paid)
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>

            {/* Right Column (4 cols): Register Promoter Card */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Onboard form */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
                <div>
                  <h2 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1">
                    <UserPlus className="w-4 h-4 text-blue-500" /> Onboard grassroots campaign
                  </h2>
                  <p className="text-[11px] text-gray-500 leading-normal">Register flyers, venues offline word of mouth, or promoter events to direct 20% referrer subscription fee attribution.</p>
                </div>

                <form onSubmit={handleAddPromoter} className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Promoter Name *</label>
                    <input
                      type="text"
                      required
                      value={newPromoterName}
                      onChange={(e) => setNewPromoterName(e.target.value)}
                      placeholder="e.g. Robert Flyer Team"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Unique Flyer/Promo Code *</label>
                    <input
                      type="text"
                      required
                      value={newPromoterCode}
                      onChange={(e) => setNewPromoterCode(e.target.value)}
                      placeholder="e.g. GROW-BOB"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs uppercase"
                    />
                    <span className="text-[9px] text-gray-400 block font-normal">Must be unique. Clients entering this code or linked dynamically map subscription commissions.</span>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Contact Email</label>
                    <input
                      type="email"
                      value={newPromoterEmail}
                      onChange={(e) => setNewPromoterEmail(e.target.value)}
                      placeholder="robert@website.com"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Contact Phone</label>
                    <input
                      type="text"
                      value={newPromoterPhone}
                      onChange={(e) => setNewPromoterPhone(e.target.value)}
                      placeholder="(555) 019-2831"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Campaign Format</label>
                    <select
                      value={newPromoterMethod}
                      onChange={(e) => setNewPromoterMethod(e.target.value as any)}
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    >
                      <option value="Flyer">Flyer Promotion</option>
                      <option value="Word of Mouth">Word of Mouth</option>
                      <option value="Event">Event/Conferences</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Commission Rate (%) *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="100"
                      value={newPromoterCommission}
                      onChange={(e) => setNewPromoterCommission(e.target.value)}
                      placeholder="20"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs font-bold text-indigo-700"
                    />
                    <span className="text-[9px] text-gray-400 block font-normal">Recurring monthly percentage of client subscriber fees. Defaults to 20%.</span>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Format Details / Notes</label>
                    <textarea
                      value={newPromoterDetails}
                      onChange={(e) => setNewPromoterDetails(e.target.value)}
                      placeholder="e.g. Distributing flyers across downtown venues during restaurant week."
                      className="w-full h-16 p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider rounded-lg h-9 shadow shrink-0 cursor-pointer"
                  >
                    Register affiliate
                  </button>
                </form>
              </div>

            </div>
          </div>
        </div>
      )}

      {activeTabName === 'laws' && (
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-200">
          <TipLawsManager />
        </div>
      )}

      {activeTabName === 'deployments' && (
        <div className="space-y-6">
          <SustainabilityDashboard scorePct={scorePct} completedItems={completedItems} totalItems={totalItems} getHealthGrade={getHealthGrade} />
          
          {user?.email === 'admin@growezy.ai' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-sm">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-1.5">
                <HeartPulse className="w-4 h-4 text-emerald-600" /> System Health Diagnostics
              </h2>
              <button onClick={checkSystemHealth} className="btn-primary text-xs" disabled={isHealthChecking}>
                {isHealthChecking ? 'Checking...' : 'Check Stripe Health'}
              </button>
              {systemHealth && (
                <pre className="text-[10px] bg-gray-50 p-3 rounded-lg overflow-x-auto text-gray-700">
                  {JSON.stringify(systemHealth, null, 2)}
                </pre>
              )}
            </div>
          )}

          {/* BLUEPRINT IMPACT SIMULATOR CARD */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6 shadow-sm">
            <div className="space-y-1 border-b border-gray-200 pb-4">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-blue-600" /> Blueprint Impact Simulator
              </h2>
              <p className="text-[11px] text-gray-500 leading-relaxed font-medium">
                Validate proposed code changes against the architectural guidelines to ensure they don't break transaction integrity.
              </p>
            </div>

            {/* Preset Select Dropdown */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Load Preset Proposal Scenario:
              </label>
              <select
                value={selectedScenario}
                onChange={(e) => handleSelectScenario(e.target.value)}
                className="input-field text-xs bg-white text-gray-800 font-medium font-bold"
              >
                <option value="">-- Choose a proposal to test --</option>
                {scenarios.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Text Area Proposal Input */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                Or Customize Code Change Proposal Text:
              </label>
              <textarea
                value={proposalText}
                onChange={(e) => {
                  setSelectedScenario('');
                  setProposalText(e.target.value);
                }}
                placeholder="e.g., Filter active transactions by source inside DashboardSummary.tsx directly..."
                className="w-full h-24 p-3 rounded-xl border border-gray-200 bg-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/40"
              />
            </div>

            {/* Run test button */}
            <div className="flex gap-2">
              <button
                onClick={runCustomSimulator}
                disabled={!proposalText.trim()}
                className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-slate-800 disabled:opacity-50 transition-all cursor-pointer shrink-0"
              >
                Simulate Appraisal
              </button>
              <button
                onClick={() => {
                  setProposalText('');
                  setSelectedScenario('');
                  setSimulatorResult(null);
                }}
                className="p-2 bg-gray-200 hover:bg-gray-300 rounded-xl text-gray-600 transition-all shrink-0"
                title="Reset Simulator"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            {/* Result Card with Animation */}
            <AnimatePresence mode="wait">
              {simulatorResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`p-4 rounded-xl border border-dashed text-xs space-y-2 ${
                    simulatorResult.status === 'PASSED' 
                      ? 'bg-emerald-50/50 border-emerald-300/80 text-emerald-800' 
                      : simulatorResult.status === 'CAUTION'
                      ? 'bg-amber-50/50 border-amber-300/80 text-amber-800'
                      : 'bg-rose-50/50 border-rose-300/80 text-rose-800'
                  }`}
                >
                  <div className="flex items-center gap-2 font-black uppercase text-xs">
                    {simulatorResult.status === 'PASSED' && <ShieldCheck className="w-5 h-5 text-emerald-600" />}
                    {simulatorResult.status === 'CAUTION' && <AlertTriangle className="w-5 h-5 text-amber-600" />}
                    {simulatorResult.status === 'FAILED' && <AlertTriangle className="w-5 h-5 text-rose-600 animate-bounce" />}
                    <span>{simulatorResult.rating}</span>
                  </div>
                  
                  {simulatorResult.ruleVioliated && (
                    <div className="py-0.5 px-2 bg-rose-200 text-rose-900 font-bold tracking-tight rounded self-start inline-block">
                      {simulatorResult.ruleVioliated}
                    </div>
                  )}

                  <p className="leading-relaxed font-semibold italic text-xs">
                    &ldquo;{simulatorResult.explanation}&rdquo;
                  </p>
                  <div className="text-[10px] text-gray-400 mt-3 pt-2 border-t border-gray-200/60 leading-normal">
                    Verdict produced instantly. This checks code properties against `AGENTS.md` boundaries in real-time.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* PWA & GOOGLE CLOUD RUN DEPLOYMENT REFERENCE ARCHIVE */}
          <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-200 space-y-6">
            <div className="border-b border-gray-100 pb-5">
              <div className="flex items-center gap-2">
                <span className="bg-emerald-50 text-emerald-800 text-[9px] font-black uppercase px-2.5 py-1 rounded border border-emerald-200/50">
                  OWNER HANDBOOK
                </span>
                <div className="text-[10px] text-gray-450 font-extrabold flex items-center gap-1">
                  <Compass className="w-3.5 h-3.5 text-emerald-500" /> Production Release Runbook
                </div>
              </div>
              <h2 className="text-xl font-black text-gray-910 tracking-tight uppercase italic mt-1.5 flex items-center gap-2">
                <Cloud className="w-5.5 h-5.5 text-gray-850" />
                Cloud Run & PWA Deployment Reference
              </h2>
              <p className="text-gray-500 text-xs font-semibold leading-relaxed">
                Everything you need to successfully launch TipValue as an installable standalone application and host it securely on Google Cloud.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Column 1: PWA Installer Benefits */}
              <div className="bg-slate-50 p-5 rounded-2xl border border-gray-200/60 space-y-3.5">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                  <Smartphone className="w-4 h-4 text-emerald-600" /> PWA Capabilities Pre-Configured
                </h3>
                <p className="text-[11px] text-gray-650 font-semibold leading-relaxed">
                  We have fully configured the application shell with continuous service worker technology and installer declarations:
                </p>
                <ul className="space-y-2 text-[11px] font-semibold text-gray-600">
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span><strong>Installer Prompts:</strong> Visitors will instantly see the browser's download/install icon in their address bar or mobile drawer.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span><strong>Offline Execution:</strong> Core assets (<code className="text-blue-600 bg-blue-50 px-1 py-0.5 rounded text-[10px]">manifest.json</code>, CSS, web icons) cache dynamically to prevent system disconnects.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span><strong>Premium SVG Launcher:</strong> High-density, scalable vector visual branding has been injected for immediate responsive homescreen presentation.</span>
                  </li>
                </ul>
              </div>

              {/* Column 2: Exact Build Actions */}
              <div className="bg-slate-50 p-5 rounded-2xl border border-gray-200/60 space-y-3.5">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                  <Terminal className="w-4 h-4 text-indigo-600" /> CLI Cloud Deployment Scripting
                </h3>
                <p className="text-[11px] text-gray-650 font-semibold leading-relaxed">
                  Quickly host TipValue on Google Cloud Run leveraging Docker container builds:
                </p>
                
                <div className="space-y-2">
                  <div className="space-y-1">
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block">1. Build & Push Image</span>
                    <div className="flex gap-1.5">
                      <code className="text-[10px] font-semibold font-mono bg-slate-200 text-gray-800 px-2 py-1 rounded block truncate overflow-hidden grow select-all">
                        gcloud builds submit --tag gcr.io/your-project-id/tipflow
                      </code>
                      <button
                        onClick={() => handleCopyText('gcloud builds submit --tag gcr.io/your-project-id/tipflow', 'gcloud_build')}
                        className="px-2 py-1 bg-slate-700 text-[9px] text-white font-bold rounded hover:bg-slate-800 uppercase shrink-0 cursor-pointer"
                      >
                        {copiedRuleId === 'gcloud_build' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider block">2. Launch Container</span>
                    <div className="flex gap-1.5">
                      <code className="text-[10px] font-semibold font-mono bg-slate-200 text-gray-800 px-2 py-1 rounded block truncate overflow-hidden grow select-all">
                        gcloud run deploy tipflow --image gcr.io/your-project-id/tipflow --platform managed --allow-unauthenticated
                      </code>
                      <button
                        onClick={() => handleCopyText('gcloud run deploy tipflow --image gcr.io/your-project-id/tipflow --platform managed --allow-unauthenticated', 'gcloud_deploy')}
                        className="px-2 py-1 bg-slate-700 text-[9px] text-white font-bold rounded hover:bg-slate-800 uppercase shrink-0 cursor-pointer"
                      >
                        {copiedRuleId === 'gcloud_deploy' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Essential Variables Summary Block */}
            <div className="p-4 bg-amber-50/50 border border-amber-200/60 rounded-2xl space-y-2">
              <h4 className="text-xs font-black text-amber-900 uppercase tracking-wider flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-600" /> Mandatory Production Environments
              </h4>
              <p className="text-[11px] text-amber-950 font-semibold leading-relaxed">
                Be absolutely sure to register these keys via the Google Cloud Run Portal's environment setup tab upon deployment:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 text-[10px] font-mono">
                <div className="bg-white p-2 border border-amber-100 rounded-lg">
                  <span className="font-extrabold text-amber-902 uppercase block text-[9px]">GEMINI_API_KEY</span>
                  <span className="text-gray-500 font-semibold block mt-0.5">Powers collective pooling analytics & reconciliation reporting</span>
                </div>
                <div className="bg-white p-2 border border-amber-100 rounded-lg">
                  <span className="font-extrabold text-amber-902 uppercase block text-[9px]">STRIPE_SECRET_KEY</span>
                  <span className="text-gray-500 font-semibold block mt-0.5">Fulfills operator subscription plans & invoices</span>
                </div>
                <div className="bg-white p-2 border border-amber-100 rounded-lg">
                  <span className="font-extrabold text-amber-902 uppercase block text-[9px]">STRIPE_WEBHOOK_SECRET</span>
                  <span className="text-gray-500 font-semibold block mt-0.5">Validates real-time payment events from Stripe portals</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {activeTabName === 'resources' && (
        <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-200">
          <ResourceManager />
        </div>
      )}

      {/* SYSTEM PLATFORM CONFIGS TAB CONTENT */}
      {activeTabName === 'configs' && (
        <div className="space-y-6">
          {/* Header Info */}
          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-2">
            <div className="text-[10px] uppercase font-black text-slate-400">Environment System configs</div>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-black text-slate-800">Global Option Lists</h2>
              <button
                onClick={handleSeedDefaults}
                className="px-3 py-1.5 bg-slate-800 text-white font-bold text-[10px] rounded hover:bg-slate-900 cursor-pointer uppercase flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Seed Default Option Sets
              </button>
            </div>
            <p className="text-xs text-gray-500 font-semibold leading-normal">
              Manage options that will populate client select lists dynamically across onboarding, payroll exports, and configurations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Column wrapper */}
            <div className="lg:col-span-4 space-y-6">
              {/* Option register form */}
              <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                <div>
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                    <Plus className="w-4 h-4 text-blue-500" /> Add dynamic config set
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-normal">Add list dropdown configurations dynamically without redeploying code.</p>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Unique Key *</label>
                    <input
                      type="text"
                      value={cKey}
                      onChange={(e) => setCKey(e.target.value)}
                      placeholder="e.g. posMerchantProvider"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Group/Category</label>
                    <input
                      type="text"
                      value={cCategory}
                      onChange={(e) => setCCategory(e.target.value)}
                      placeholder="e.g. General"
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Select Options (comma-separated list) *</label>
                    <textarea
                      value={cOptions}
                      onChange={(e) => setCOptions(e.target.value)}
                      placeholder="ADP, Gusto, Paychex"
                      className="w-full h-16 p-2 border border-gray-200 rounded-lg text-xs"
                    />
                  </div>

                  <button
                    onClick={handleAddConfig}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider rounded-lg shadow shrink-0 cursor-pointer"
                  >
                    Create Config Set
                  </button>
                </div>
              </div>

              {/* Tipping Benchmark Settings Card */}
              <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <Compass className="text-indigo-600 w-5 h-5 animate-pulse" />
                  <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">
                    Benchmark Setting
                  </h3>
                </div>
                <p className="text-[10px] text-gray-500 leading-normal">
                  Customize the minimum active peer cohort geographical sector sample size count requirement to construct anon comparison indices.
                </p>

                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase block">Min Peer Locations Requirement</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={minSampleSize}
                      onChange={(e) => setMinSampleSize(Number(e.target.value) || 1)}
                      className="w-full p-2 border border-gray-200 rounded-lg text-xs font-semibold"
                    />
                  </div>

                  <button
                    onClick={handleSaveBenchmarkSettings}
                    disabled={isSavingBenchmarkSettings}
                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-lg shadow shrink-0 cursor-pointer disabled:opacity-50"
                  >
                    {isSavingBenchmarkSettings ? 'Updating...' : 'Update Requirement'}
                  </button>

                  <span className="text-[9px] text-slate-400 font-medium block">
                    Default is 10. For sandbox/testing purposes, set this value to 1 or 2 in order to expose and display benchmarks assuming a client has paid for the plan.
                  </span>
                </div>
              </div>
            </div>

            {/* Option configurations listings */}
            <div className="lg:col-span-8 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Active configuration option mappings</h3>
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Find config key..."
                    value={configSearchTerm}
                    onChange={(e) => setConfigSearchTerm(e.target.value)}
                    className="w-full p-1.5 pl-8 border border-gray-200 rounded-lg text-xs"
                  />
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                </div>
              </div>

              {isConfigsLoading ? (
                <div className="text-center py-8 text-xs text-slate-400">Loading configurations...</div>
              ) : platformConfigs.length === 0 ? (
                <div className="text-center py-8 border border-dashed rounded-xl border-gray-200 space-y-1">
                  <p className="text-xs text-gray-400 font-semibold">No platform options registered.</p>
                  <button
                    onClick={handleSeedDefaults}
                    className="text-[10px] text-blue-600 hover:underline font-bold mt-1 block mx-auto uppercase"
                  >
                    Click here to seed standard systems defaults
                  </button>
                </div>
              ) : (
                <div className="space-y-4 max-h-[460px] overflow-y-auto pr-1">
                  {platformConfigs
                    .filter(c => c && c.key && c.key !== 'benchmark_settings' && Array.isArray(c.options))
                    .filter(c => !configSearchTerm || c.key.toLowerCase().includes(configSearchTerm.toLowerCase()))
                    .map(config => (
                      <div key={config.key} className="p-4 rounded-xl border border-gray-200 space-y-3 bg-slate-50/50 hover:border-gray-300 transition-all">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-extrabold text-xs text-indigo-700 font-mono select-all font-bold">KEY: {config.key}</h4>
                            <span className="text-[9px] bg-slate-200 text-slate-705 font-bold px-2 py-0.5 rounded uppercase mt-1 inline-block">
                              Category: {config.category || 'General'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleDeleteConfig(config.key)}
                            className="text-gray-400 hover:text-rose-500 transition-colors p-1"
                            title="Delete option"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        <div className="space-y-1.5 pt-1">
                          <label className="text-[9px] font-bold text-gray-500 uppercase block">Drop-down Select Options</label>
                          <input
                            type="text"
                            defaultValue={(config.options || []).join(', ')}
                            onBlur={(e) => {
                              const updatedOptions = e.target.value.split(',').map(o => o.trim()).filter(Boolean);
                              if (JSON.stringify(config.options || []) !== JSON.stringify(updatedOptions)) {
                                handleSaveConfig({ ...config, options: updatedOptions });
                              }
                            }}
                            placeholder="Enter options split by commas..."
                            className="w-full p-2 border border-gray-200 bg-white rounded-lg text-xs font-semibold text-gray-750"
                          />
                          <span className="text-[8px] text-gray-400 font-normal block italic">Change and click away (losses focus) to immediately save changes.</span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RECORD PAYOUT MODAL */}
      {showPayoutModal && payoutPromoter && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-[2rem] p-6 max-w-lg w-full border border-slate-200 shadow-2xl space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <span className="bg-indigo-50 text-indigo-700 font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-indigo-200/55">
                  Promoter Ledger System
                </span>
                <h3 className="text-sm font-black text-slate-800 mt-1">Payouts & History: {payoutPromoter.name}</h3>
                <p className="text-[10px] text-gray-500 font-semibold font-mono">CODE: {payoutPromoter.code} | Total Paid: {formatCurrency(payoutPromoter.totalPaidCommissions || 0)}</p>
              </div>
              <button
                onClick={() => {
                  setShowPayoutModal(false);
                  setPayoutPromoter(null);
                }}
                className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 rounded font-black text-[10px] text-gray-600 transition-colors uppercase shrink-0 cursor-pointer"
              >
                Close
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Record a Payment */}
              <div className="space-y-3 bg-slate-50/50 p-4 rounded-xl border border-gray-100">
                <h4 className="text-[10px] font-black uppercase text-gray-800 tracking-wider">Record payout</h4>
                <form onSubmit={handleRecordPayout} className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-550 uppercase block">Payment Amount ($) *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      step="any"
                      value={payoutAmount}
                      onChange={(e) => setPayoutAmount(e.target.value)}
                      placeholder="e.g. 150"
                      className="w-full p-2 border border-gray-200 bg-white rounded-lg text-xs font-bold"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-bold text-gray-550 uppercase block">Reference/Notes</label>
                    <input
                      type="text"
                      value={payoutNotes}
                      onChange={(e) => setPayoutNotes(e.target.value)}
                      placeholder="e.g. PayPal ref 512"
                      className="w-full p-2 border border-gray-200 bg-white rounded-lg text-xs font-semibold"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2 bg-slate-800 hover:bg-slate-900 text-white font-black text-[10px] uppercase tracking-wider rounded cursor-pointer"
                  >
                    Log Payout
                  </button>
                </form>
              </div>

              {/* Payout History Ledger */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-black uppercase text-slate-800 tracking-wider">Payout History</h4>
                {isPayoutsLoading ? (
                  <div className="text-[10px] text-slate-400">Syncing ledger...</div>
                ) : promoterPayouts.length === 0 ? (
                  <div className="text-[10px] py-10 text-center text-gray-400 border border-dashed rounded-xl font-semibold">No payouts logged.</div>
                ) : (
                  <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                    {promoterPayouts.map(p => (
                      <div key={p.id} className="p-2 bg-white border border-gray-100 rounded-lg text-[10px] font-semibold leading-normal">
                        <div className="flex justify-between items-center">
                          <span className="font-extrabold text-slate-850">${p.amount.toFixed(2)}</span>
                          <span className="text-gray-400 text-[8px]">{new Date(p.date).toLocaleDateString()}</span>
                        </div>
                        {p.notes && <p className="text-gray-500 italic mt-0.5 text-[9px]">&ldquo;{p.notes}&rdquo;</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      </div> {/* Closing super-admin-main-content */}

      {/* FLYER CUSTOMIZER MODAL */}
      {showFlyerModal && flyerPromoter && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in printable-modal-backdrop">
          <div className="bg-white rounded-[2rem] p-6 max-w-6xl w-full h-[90vh] overflow-y-auto border border-slate-200 shadow-2xl flex flex-col justify-between space-y-6 printable-modal-card">
            
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-gray-100 pb-4 no-print">
              <div>
                <span className="bg-indigo-50 text-indigo-700 font-extrabold text-[9px] uppercase tracking-widest px-2.5 py-0.5 rounded-full border border-indigo-200/55">
                  Grassroots Campaign Flyer Studio
                </span>
                <h3 className="text-sm font-black text-slate-800 mt-1">Printable Promo Flyer for {flyerPromoter.name}</h3>
                <p className="text-[10px] text-gray-500 font-semibold font-mono">
                  Promoter Code: {flyerPromoter.code} | Rate: {flyerPromoter.commissionPercentage ?? 20}%
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.print()}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded-xl font-extrabold text-[10px] text-white transition-colors uppercase shrink-0 cursor-pointer"
                >
                  Print Flyer
                </button>
                <button
                  onClick={() => {
                    setShowFlyerModal(false);
                    setFlyerPromoter(null);
                  }}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-xl font-extrabold text-[10px] text-gray-605 transition-colors uppercase shrink-0 cursor-pointer"
                >
                  Close Studio
                </button>
              </div>
            </div>

            {/* Customizer + Preview Body */}
            <div className="grow space-y-6">

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Customizer Sidebar */}
                <div className="lg:col-span-4 bg-slate-50 border border-gray-150 p-5 rounded-2xl space-y-5 no-print">
                  <div className="space-y-1">
                    <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Palette className="w-4 h-4 text-indigo-600" /> Visual & Message Controls
                    </h4>
                    <p className="text-[10px] text-gray-450 leading-relaxed">Customize the printed theme style and invitation copy below to generate a beautiful, scan-ready partner flyer.</p>
                  </div>

                  {/* Theme Selector */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">Visual Style Theme</label>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { id: 'indigo', name: 'Indigo', color: 'bg-indigo-600 border-indigo-205' },
                        { id: 'emerald', name: 'Emerald', color: 'bg-emerald-600 border-emerald-205' },
                        { id: 'charcoal', name: 'Charcoal', color: 'bg-slate-900 border-slate-300' },
                        { id: 'amber', name: 'Warm Amber', color: 'bg-amber-600 border-amber-205' }
                      ].map(theme => (
                        <button
                          key={theme.id}
                          onClick={() => setFlyerStyle(theme.id as any)}
                          className={`flex flex-col items-center gap-1 p-2 border bg-white rounded-xl hover:bg-slate-50 transition-all cursor-pointer ${
                            flyerStyle === theme.id ? 'border-slate-800 ring-2 ring-slate-200' : 'border-gray-250'
                          }`}
                        >
                          <span className={`w-5 h-5 rounded-full ${theme.color} shadow-inner block`} />
                          <span className="text-[9px] font-bold text-gray-500">{theme.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* CTA Text Customizer */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-550 uppercase tracking-wider block">Special Promotional Call-To-Action</label>
                    <textarea
                      rows={2}
                      value={customCta}
                      onChange={(e) => setCustomCta(e.target.value)}
                      placeholder="e.g. Claim Your 14-Day Free Trial of TipValue AI"
                      className="w-full p-2.5 border border-gray-200 bg-white rounded-xl text-xs font-semibold focus:ring-2 focus:ring-indigo-150 focus:border-indigo-500 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Personalized Footnote */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-550 uppercase tracking-wider block">Personalized Footer Note</label>
                    <textarea
                      rows={2}
                      value={customNote}
                      onChange={(e) => setCustomNote(e.target.value)}
                      placeholder="e.g. Experience automated, dispute-free tip pooling with zero credit card required."
                      className="w-full p-2.5 border border-gray-200 bg-white rounded-xl text-xs font-semibold focus:ring-2 focus:ring-indigo-150 focus:border-indigo-500 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Code Block */}
                  <div className="p-3 bg-white border border-gray-200 rounded-xl space-y-1 font-semibold leading-none">
                    <span className="text-[9px] font-black uppercase text-gray-400 tracking-wider">Campaign Code Embedding</span>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-mono text-xs font-black text-indigo-750 uppercase bg-slate-50 border border-gray-250 px-2 py-1 rounded">
                        {flyerPromoter.code}
                      </span>
                      <span className="text-[9px] text-gray-400 font-semibold italic">Auto-encoded in QR code</span>
                    </div>
                  </div>

                  {/* Print instructions */}
                  <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl text-[10px] leading-relaxed text-indigo-900 font-medium space-y-1 font-semibold">
                    <p className="font-black uppercase text-[9px] text-indigo-700 tracking-wider flex items-center gap-1">
                      <Printer className="w-3.5 h-3.5" /> Print Settings Tips:
                    </p>
                    <ul className="list-disc list-inside pl-0.5 space-y-0.5 text-[9px]">
                      <li>Select <strong>Save as PDF</strong> or a physical printer.</li>
                      <li>Check <strong>"Background graphics"</strong> to show colored headers/banners.</li>
                      <li>Set margins to <strong>"None"</strong> or <strong>"Default"</strong>.</li>
                    </ul>
                  </div>

                  {/* Sandbox / Iframe Warning Panel */}
                  {isInIframe && (
                    <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-[10px] text-amber-900 leading-relaxed font-semibold space-y-2 font-sans no-print">
                      <p className="font-black uppercase text-[9px] text-amber-700 tracking-wider flex items-center gap-1">
                        ⚠️ Sandbox Preview Limitation
                      </p>
                      <p>
                        Your browser blocks print triggers inside interactive preview frames. To print or save your custom flyer as a PDF, please open this portal in a new full browser tab.
                      </p>
                      <a
                        href="/super-admin-portal"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 w-full h-9 bg-amber-600 hover:bg-amber-700 text-white font-black text-[9px] uppercase tracking-wider rounded-lg transition-colors cursor-pointer"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open Portal in New Tab
                      </a>
                    </div>
                  )}

                  {/* Print Action */}
                  <button
                    onClick={() => {
                      if (isInIframe) {
                        alert("To print, please open this app in a new full tab using the 'Open Portal in New Tab' button below. Browser security restricts printing from inside live-preview panels.");
                        return;
                      }
                      window.print();
                    }}
                    className={`w-full h-11 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow flex items-center justify-center gap-2 cursor-pointer touch-none select-none ${
                      isInIframe ? 'bg-slate-400 hover:bg-slate-500 opacity-80' : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                  >
                    <Printer className="w-4 h-4" /> {isInIframe ? "Print Disabled (Open New Tab)" : "Print / Save as PDF"}
                  </button>
                </div>

                {/* Right Column: Letter Sheet Preview */}
                <div className="lg:col-span-8 space-y-4 printable-flyer-outer">

                  {/* Printable Sheet */}
                  <div id="printable-flyer-page-id" className="printable-flyer-page aspect-[8.5/11] bg-white border border-gray-200 rounded-[2rem] shadow-xl p-8 sm:p-12 flex flex-col justify-between text-slate-800 relative select-none">
                    
                    {/* Style accent top bar */}
                    <div className={`absolute top-0 left-0 right-0 h-4 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryBg}`} />

                    {/* Top Header Row */}
                    <div className="flex justify-between items-center pb-6 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-xl ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryBg} text-white flex items-center justify-center shadow`}>
                          <Zap className="w-4.5 h-4.5" />
                        </div>
                        <div>
                          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Grassroots Campaign Partner</span>
                          <h1 className="text-sm font-black text-slate-900 tracking-tight leading-none mt-0.5 font-sans">TipValue AI</h1>
                        </div>
                      </div>
                      
                      <span className={`text-[9px] font-black uppercase tracking-widest border px-2.5 py-1 rounded-full ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} bg-slate-50`}>
                        Official Hospitality Partner
                      </span>
                    </div>

                    {/* Main Flyer Message Hook */}
                    <div className="space-y-3 pt-6 text-center md:text-left">
                      <h2 className="text-xl sm:text-2.5xl font-black text-slate-900 tracking-tight leading-tight max-w-2xl font-sans">
                        Stop Spending Hours Pooling Tips. <span className={styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}>Automate Allocations in Seconds.</span>
                      </h2>
                      <p className="text-[11px] sm:text-xs text-gray-500 font-semibold leading-relaxed max-w-2xl font-sans">
                        Automate daily tip pooling calculations, pro-rate split shares using exact labor hours, provide complete allocation transparency to your staff, and reduce shift handoff disputes completely.
                      </p>
                    </div>

                    {/* Traditional Way vs TipFlow Way (Side by Side comparison) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 sm:py-6">
                      {/* Traditional */}
                      <div className="p-4 bg-rose-50/40 rounded-2xl border border-rose-100/50 space-y-2">
                        <span className="text-[9px] font-black text-rose-600 uppercase tracking-widest block font-sans">The Manual Spreadsheet Headache</span>
                        <ul className="space-y-1 text-[10px] text-gray-500 font-semibold leading-normal font-sans">
                          <li className="flex items-start gap-1.5">
                            <span className="text-rose-500 shrink-0 select-none">✕</span>
                            <span>Hours wasted typing in back clock-in hours and shift splits</span>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className="text-rose-500 shrink-0 select-none">✕</span>
                            <span>Prone to human entry mistakes that create staff friction</span>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className="text-rose-500 shrink-0 select-none">✕</span>
                            <span>No clear audit trails or logs for labor compliance records</span>
                          </li>
                        </ul>
                      </div>

                      {/* TipFlow */}
                      <div className={`p-4 rounded-2xl border ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} space-y-2`}>
                        <span className={`text-[9px] font-black ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} uppercase tracking-widest block font-sans`}>The Automated TipValue Standard</span>
                        <ul className="space-y-1 text-[10px] text-gray-655 font-semibold leading-normal font-sans">
                          <li className="flex items-start gap-1.5">
                            <span className={`${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} font-bold shrink-0 select-none`}>✓</span>
                            <span>Precision split formulas calculated instantly and pro-rated</span>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className={`${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} font-bold shrink-0 select-none`}>✓</span>
                            <span>Transparent portals let staff verify their allocations easily</span>
                          </li>
                          <li className="flex items-start gap-1.5">
                            <span className={`${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} font-bold shrink-0 select-none`}>✓</span>
                            <span>Immutable database logs ensuring bulletproof audit oversight</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Major Platform Features (Core Showcase Grid) */}
                    <div className="space-y-3.5">
                      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-1.5 font-sans">
                        Core Platform Capabilities & Features
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        
                        {/* Feature 1 */}
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                            <Layers className="w-4 h-4" />
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide font-sans">Flexible Pooling Rules</h4>
                            <p className="text-[9px] text-gray-400 font-semibold leading-relaxed font-sans">
                              Easily pro-rate splits evenly, by actual shift hours worked, role points, or custom contribution tiers.
                            </p>
                          </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                            <Zap className="w-4 h-4" />
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide font-sans">Multi-Source Tip Inputs</h4>
                            <p className="text-[9px] text-gray-400 font-semibold leading-relaxed font-sans">
                              Seamlessly capture self-reported employee tips, manager-entered cash tip jars, and point-of-sale entries.
                            </p>
                          </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide font-sans">Rigorous Audit & Review</h4>
                            <p className="text-[9px] text-gray-400 font-semibold leading-relaxed font-sans">
                              Maintain immutable allocation trails with manager review timestamps, ensuring compliance and high-fidelity tracking.
                            </p>
                          </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-lg ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} flex items-center justify-center shrink-0 ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText}`}>
                            <Users className="w-4 h-4" />
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-[11px] font-black text-slate-800 uppercase tracking-wide font-sans">Self-Service Portals & Disputes</h4>
                            <p className="text-[9px] text-gray-400 font-semibold leading-relaxed font-sans">
                              Let employees log in securely to verify shift shares and flag any discrepancy for instant manager resolution.
                            </p>
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* Call to Action & QR Code Section (Large highlight strip at bottom) */}
                    <div className={`mt-6 p-5 sm:p-6 rounded-[1.5rem] border ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].lightBg} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} flex flex-col md:flex-row items-center justify-between gap-6 w-full`}>
                      
                      <div className="space-y-1.5 text-center md:text-left flex-1 min-w-0 max-w-full">
                        <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-white border ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].borderColor} ${styleConfigs[flyerStyle as 'indigo' | 'emerald' | 'charcoal' | 'amber'].primaryText} inline-block`}>
                          Exclusive Campaign Invitation
                        </span>
                        <h4 className="text-sm font-black text-slate-800 leading-tight">
                          {customCta}
                        </h4>
                        {customNote && (
                          <p className="text-[9px] text-gray-500 font-semibold leading-normal">
                            {customNote}
                          </p>
                        )}
                      </div>

                      {/* Ticket / Scan QR Code Box */}
                      <div className="flex items-center gap-4 bg-white p-3 rounded-xl border border-gray-150 shadow-inner w-full md:w-auto justify-center md:justify-start">
                        {/* QR Code Container */}
                        <div className="text-center space-y-1 shrink-0 font-semibold">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`https://tipvalue.com/?code=${flyerPromoter.code}`)}`}
                            alt="Referral QR Code"
                            className="w-16 h-16 border border-gray-100 rounded p-0.5 block mx-auto"
                            referrerPolicy="no-referrer"
                          />
                          <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block leading-none">Scan to signup</span>
                        </div>

                        {/* Separator */}
                        <div className="w-px h-12 bg-gray-200" />

                        {/* Promo Code Info */}
                        <div className="space-y-0.5 text-center md:text-left font-semibold">
                          <span className="text-[7px] font-black uppercase tracking-wider text-gray-400 block">Or Enter Promo Code</span>
                          <div className="font-mono text-sm font-black tracking-wider text-slate-800 uppercase px-2 py-0.5 bg-slate-50 border border-gray-200 rounded text-center">
                            {flyerPromoter.code}
                          </div>
                          <span className="text-[7px] font-semibold text-gray-400 block leading-tight mt-0.5 max-w-[100px]">redeems 30 days free trial benefit</span>
                        </div>
                      </div>

                    </div>

                    {/* Footer Subtext (Legals & Info) */}
                    <div className="pt-4 border-t border-gray-150 flex flex-col md:flex-row justify-between items-center text-[8px] text-gray-400 font-bold font-mono uppercase tracking-wide gap-2">
                      <span>Start automating in under 5 minutes • tipvalue.com</span>
                      <span>Compliance-Validated & Safe Over Cloud SSL</span>
                    </div>

                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      )}

    </>
  );
};
